import React, {MouseEvent} from 'react';
import styled from 'styled-components';
import StyledPaper from 'components/Paper';
import VLANLegendItem from './VLANLegendItem';
import SGBody from 'components/Typography/SGBody';
import SGTitle from 'components/Typography/SGTitle';
import Button from '@material-ui/core/Button';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import RefreshIcon from '@material-ui/icons/Refresh';
import VLANLegendToolTip from './VLANLegendTooltip';
import {VlanLegendData} from '../interfaces/SwitchConnectivityState';
import SwitchConnectivityState from '../interfaces/SwitchConnectivityState';

const StyledLegendContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  row-gap: 8px;
`;

const StyledLegendPaper = styled(StyledPaper)`
  overflow-x: auto;
  transition: 100ms ease-in-out;
  box-shadow: none;
`;

const StyledLegendItemContainer = styled.div<{$legendRows: number}>`
  display: grid;
  grid-template-columns: auto auto auto auto;
  grid-template-rows: repeat(${props => props.$legendRows}, auto);
  grid-auto-flow: column;
  column-gap: 8px;
  text-align: left;
`;

const StyledAccordion = styled(Accordion)`
  background-color: transparent;
  padding: 0;

  &.MuiPaper-elevation1 {
    box-shadow: none;
  }
  .MuiAccordionSummary-root {
    padding-left: 0px;
  }
  .MuiAccordionSummary-root.Mui-expanded {
    min-height: 48px;
  }
  .MuiAccordionSummary-content {
    margin: 0px 0;
    align-items: center;
    p {
      margin-right: 8px;
    }
  }
  .MuiAccordionSummary-content.Mui-expanded {
    margin: 0px 0;
  }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  flex-direction: column;
  padding-left: 0px;
`;

const StyledExpandMoreIcon = styled(ExpandMoreIcon)`
  color: ${props => props.theme.text};
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledButton = styled(Button)`
  color: ${props => props.theme.text};
  padding: 0;
  text-transform: none;
  text-decoration: underline;
  margin-left: auto;
`;

const StyledRefreshButton = styled(Button)`
  color: ${props => props.theme.disabledStateBlue};
  min-width: fit-content;
  padding: 0;
  margin-left: auto;
`;

interface VLANLegendProps {
  switchStackName: string;
  switchState: SwitchConnectivityState;
  idfName?: null | string;
  handleClearLegendItems: () => void;
  onLegendItemSelectionChanged: (
    switchStackName: string,
    vlanNumber: string,
    isSelected: boolean
  ) => void;
  refresh: () => void;
}

export default function VLANLegend({
  switchStackName,
  switchState,
  idfName,
  handleClearLegendItems,
  onLegendItemSelectionChanged,
  refresh,
}: VLANLegendProps) {
  const filteredVLANs = switchState.legendData.filter(
    vlan => vlan.switchDeviceName === switchStackName
  );

  const uniqueVLANs = filteredVLANs.filter((vlan, index, legendArray) => {
    return legendArray.findIndex(obj2 => obj2.shortDescription === vlan.shortDescription) === index;
  });

  const legendRows = Math.ceil(uniqueVLANs.length / 4);
  const legendItems = uniqueVLANs.map((vlan: VlanLegendData) => {
    return (
      <VLANLegendItem
        key={vlan.vlanNumber}
        vlanNumber={vlan.vlanNumber}
        shortDescription={vlan.shortDescription}
        color={vlan.color}
        isSelected={vlan.isSelected}
        onLegendItemSelectionChanged={(vlanNumber, isSelected) =>
          onLegendItemSelectionChanged(switchStackName, vlanNumber, isSelected)
        }
      />
    );
  });

  function handleRefresh(evt: React.SyntheticEvent<HTMLButtonElement>) {
    evt.preventDefault();
    evt.stopPropagation();
    refresh();
  }

  return (
    <StyledLegendPaper>
      <StyledAccordion defaultExpanded>
        <AccordionSummary
          expandIcon={<StyledExpandMoreIcon />}
          aria-controls={'switch-vlan'}
          id={'switch-vlan'}
        >
          {idfName && (
            <SGTitle variant={'t3'} className={'text-active'}>
              {idfName}
            </SGTitle>
          )}
          <SGTitle variant={'t3'} className={'text-spacing text-color'}>
            VLAN LEGEND
          </SGTitle>
          <VLANLegendToolTip />
          <StyledRefreshButton onClick={handleRefresh}>
            <RefreshIcon />
          </StyledRefreshButton>
        </AccordionSummary>
        <StyledAccordionDetails>
          <StyledLegendContainer>
            <StyledHeader>
              <SGBody>You can click on any VLAN to filter the ports in the Switch Diagram.</SGBody>
              {switchState.selectedVLANs.some(el => el.switchStackName === switchStackName) && (
                <StyledButton onClick={handleClearLegendItems}>
                  <SGBody variant={'b4'}>Clear Filters</SGBody>
                </StyledButton>
              )}
            </StyledHeader>
            <StyledLegendItemContainer $legendRows={legendRows}>
              {legendItems}
            </StyledLegendItemContainer>
          </StyledLegendContainer>
        </StyledAccordionDetails>
      </StyledAccordion>
    </StyledLegendPaper>
  );
}
